import { useState } from "react";
import axios from "axios";
import { useLang } from "../i18n/LanguageContext";
import { Plane, Wallet, Smartphone, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PASS_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/9e1313c0-9298-432e-9d95-61137a646d40/images/39de06faec372db486b4328d378238e1e67583ddde69fb48fc8a254df6f293a0.png";

const SeatGrid = ({ legend }) => {
  // Six seats: 2 rows x 3 columns, with center label denoting aisle
  const seats = [
    { id: "01A", state: "available" },
    { id: "01B", state: "taken" },
    { id: "01C", state: "available" },
    { id: "02A", state: "available" },
    { id: "02B", state: "highlight" },
    { id: "02C", state: "taken" },
  ];

  return (
    <div className="w-full">
      <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto">
        {seats.map((s) => (
          <div
            key={s.id}
            data-testid={`seat-${s.id}`}
            className={`seat ${s.state}`}
            aria-label={`Seat ${s.id} ${s.state}`}
          >
            {s.id}
          </div>
        ))}
      </div>
      <div className="mt-6 flex items-center justify-center gap-5 text-[10px] font-mono-utility tracking-widest uppercase text-[#5b664f]">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm border border-[#5b664f] bg-[#f9f6f0]" />
          {legend[0]}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-[#a64b2a]" />
          {legend[1]}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-[#f2ebe1] border border-[#e5d9c5]" />
          {legend[2]}
        </span>
      </div>
    </div>
  );
};

const BoardingPass = ({ pass }) => {
  return (
    <div className="relative bg-[#f9f6f0] border border-[#e5d9c5] rounded-md overflow-hidden">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-dashed border-[#e5d9c5] bg-[#2c1e16] text-[#f9f6f0]">
        <div className="flex items-center gap-2 font-mono-utility text-[10px] tracking-[0.25em] uppercase">
          <Plane className="w-3.5 h-3.5" strokeWidth={1.5} />
          {pass.airline}
        </div>
        <span className="font-mono-utility text-[10px] tracking-[0.25em] uppercase opacity-80">
          {pass.boarding_label}
        </span>
      </div>

      <div className="flex">
        {/* Main */}
        <div className="flex-1 p-6 md:p-7">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="font-mono-utility text-[10px] tracking-[0.25em] uppercase text-[#5b664f] mb-1">
                {pass.from_prefix}
              </div>
              <div className="font-serif-display text-3xl md:text-4xl leading-none text-[#2c1e16]">
                {pass.from}
              </div>
              <div className="text-xs text-[#2c1e16]/60 mt-1">
                {pass.from_label}
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-[#a64b2a]" strokeWidth={1.5} />
            <div className="text-right">
              <div className="font-mono-utility text-[10px] tracking-[0.25em] uppercase text-[#5b664f] mb-1">
                {pass.to_prefix}
              </div>
              <div className="font-serif-display text-3xl md:text-4xl leading-none text-[#2c1e16]">
                {pass.to}
              </div>
              <div className="text-xs text-[#2c1e16]/60 mt-1">
                {pass.to_label}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 border-t border-dashed border-[#e5d9c5] pt-4">
            <div>
              <div className="font-mono-utility text-[9px] tracking-[0.2em] uppercase text-[#5b664f]">
                {pass.passenger_label}
              </div>
              <div className="text-sm text-[#2c1e16] mt-1 truncate">
                {pass.passenger}
              </div>
            </div>
            <div>
              <div className="font-mono-utility text-[9px] tracking-[0.2em] uppercase text-[#5b664f]">
                {pass.seat_label}
              </div>
              <div className="text-sm text-[#2c1e16] mt-1">{pass.seat}</div>
            </div>
            <div>
              <div className="font-mono-utility text-[9px] tracking-[0.2em] uppercase text-[#5b664f]">
                {pass.date_label}
              </div>
              <div className="text-sm text-[#2c1e16] mt-1">{pass.date}</div>
            </div>
          </div>
        </div>

        {/* Perforation */}
        <div className="perforation w-2" />

        {/* Stub */}
        <div className="w-28 p-4 bg-[#f2ebe1] flex flex-col items-center justify-between">
          <div className="font-mono-utility text-[9px] tracking-[0.2em] uppercase text-[#5b664f] text-center">
            {pass.gate_label}
          </div>
          <div className="font-serif-display text-xl text-[#2c1e16] text-center leading-tight">
            {pass.gate}
          </div>
          <div className="w-full">
            <div className="flex gap-[2px] mt-2">
              {Array.from({ length: 26 }).map((_, i) => (
                <span
                  key={i}
                  className="flex-1 bg-[#2c1e16]"
                  style={{ height: 24, opacity: i % 2 === 0 ? 1 : 0.6 }}
                />
              ))}
            </div>
            <div className="mt-1 text-center font-mono-utility text-[8px] tracking-[0.2em] text-[#2c1e16]/70">
              {pass.flight}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const BoardingTeaser = () => {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setMessage(t.boarding.notify_error);
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/newsletter`, { email });
      setStatus("success");
      setMessage(t.boarding.notify_success);
      setEmail("");
    } catch (err) {
      setStatus("error");
      setMessage(t.boarding.notify_error);
    }
  };

  return (
    <section
      id="boarding"
      data-testid="boarding-section"
      className="relative py-24 md:py-36 bg-[#2c1e16] text-[#f9f6f0] overflow-hidden"
    >
      {/* subtle grain */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(rgba(249,246,240,0.6) 1px, transparent 1px)",
          backgroundSize: "3px 3px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-6 md:gap-12 mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-4 flex items-start gap-3">
            <span className="inline-block w-12 h-px bg-[#a64b2a] mt-3" />
            <div className="flex items-center gap-2">
              <span className="pulse-dot w-1.5 h-1.5 rounded-full bg-[#a64b2a]" />
              <span
                data-testid="boarding-overline"
                className="font-mono-utility text-[10px] tracking-[0.3em] uppercase text-[#a64b2a]"
              >
                {t.boarding.overline}
              </span>
            </div>
          </div>
          <div className="col-span-12 md:col-span-8">
            <h2
              data-testid="boarding-title"
              className="font-serif-display text-4xl md:text-6xl leading-[1.05] tracking-tight mb-6"
            >
              {t.boarding.title}
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-[#f9f6f0]/75 max-w-2xl">
              {t.boarding.sub}
            </p>
          </div>
        </div>

        {/* Bento */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {/* Seat map card */}
          <div
            data-testid="seatmap-card"
            className="md:col-span-5 bg-[#f9f6f0] text-[#2c1e16] rounded-md p-7 md:p-10 border border-[#e5d9c5]"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono-utility text-[10px] tracking-[0.25em] uppercase text-[#a64b2a]">
                {t.boarding.seatmap_title}
              </span>
              <span className="font-mono-utility text-[10px] tracking-[0.25em] uppercase text-[#5b664f]">
                {t.boarding.seatmap_meta_live}
              </span>
            </div>
            <p className="text-sm text-[#2c1e16]/60 mb-8">
              {t.boarding.seatmap_sub}
            </p>

            {/* fuselage */}
            <div className="relative mx-auto max-w-sm border border-[#e5d9c5] rounded-[40px] p-6 bg-[#f9f6f0]">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-16 h-6 bg-[#f9f6f0] border border-[#e5d9c5] rounded-t-full" />
              <SeatGrid legend={t.boarding.seat_legend} />
              <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-24 h-6 bg-[#f9f6f0] border border-[#e5d9c5] rounded-b-full" />
            </div>

            <div className="mt-6 flex items-center justify-between text-[10px] font-mono-utility tracking-widest uppercase text-[#5b664f]">
              <span>{t.boarding.seatmap_cabin}</span>
              <span>{t.boarding.seatmap_class}</span>
            </div>
          </div>

          {/* Boarding pass + capture */}
          <div className="md:col-span-7 flex flex-col gap-6 md:gap-8">
            <div data-testid="boarding-pass-card">
              <BoardingPass pass={t.boarding.pass} />
              <div className="mt-3 hidden md:block">
                <img
                  src={PASS_IMAGE}
                  alt=""
                  aria-hidden
                  className="hidden"
                />
              </div>
            </div>

            <div
              data-testid="notify-card"
              className="rounded-md border border-[#f9f6f0]/15 p-6 md:p-8 bg-[#3a2820]"
            >
              <div className="flex items-center gap-2 mb-3 font-mono-utility text-[10px] tracking-[0.25em] uppercase text-[#a64b2a]">
                <span className="pulse-dot w-1.5 h-1.5 rounded-full bg-[#a64b2a]" />
                {t.boarding.status}
              </div>
              <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
                <input
                  data-testid="notify-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.boarding.notify_placeholder}
                  className="flex-1 bg-transparent border border-[#f9f6f0]/20 rounded-full px-5 py-3 text-sm text-[#f9f6f0] placeholder:text-[#f9f6f0]/40 focus:outline-none focus:border-[#a64b2a] transition-colors"
                />
                <button
                  data-testid="notify-submit-btn"
                  type="submit"
                  disabled={status === "loading"}
                  className="inline-flex items-center justify-center gap-2 bg-[#a64b2a] hover:bg-[#f9f6f0] hover:text-[#2c1e16] text-[#f9f6f0] px-6 py-3 rounded-full text-xs font-mono-utility tracking-[0.2em] uppercase transition-colors disabled:opacity-50"
                >
                  {status === "loading" ? "..." : t.boarding.notify_cta}
                  <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </form>

              {status !== "idle" && status !== "loading" && (
                <p
                  data-testid="notify-message"
                  className={`mt-4 text-sm ${
                    status === "success"
                      ? "text-[#d2b48c]"
                      : "text-[#e8a987]"
                  }`}
                >
                  {message}
                </p>
              )}

              <div className="mt-6 flex flex-wrap items-center gap-4 text-[10px] font-mono-utility tracking-[0.25em] uppercase text-[#f9f6f0]/60">
                <span className="flex items-center gap-1.5">
                  <Wallet className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {t.boarding.wallet[0]}
                </span>
                <span className="flex items-center gap-1.5">
                  <Smartphone className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {t.boarding.wallet[1]}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
